import {
  useMutation,
  useQueryClient,
  type QueryClient,
  type UseMutationOptions,
} from "@tanstack/react-query";
import { api } from "./api";
import { queryKeys } from "./queryKeys";
import type { TaskRow, TaskStatus } from "@shared/types";

// All task-affecting mutations should run this on success. Touching a task
// can shift four cache surfaces, and earlier we drifted on which keys got
// invalidated per call site. Putting the invalidate list here means a new
// surface only needs to be added once.
//
// Why each key:
//   ['tasks', projectId]  — board / list views of the project the task lives in
//   ['task', taskId]      — the detail view (tags, full body, etc.)
//   ['subtasks', taskId]  — if this task is/was a parent
//   ['me', 'tasks']       — assigned-to-me queue
//   ['me', 'watching']    — created-by-me / commented-on queue
//   ['activity', projectId] — the project's activity feed
//   ['inbox', *]          — the bell badge + inbox list
export function invalidateForTask(
  qc: QueryClient,
  args: { projectId?: string; taskId?: string },
): void {
  if (args.projectId) {
    qc.invalidateQueries({ queryKey: queryKeys.tasksFor(args.projectId) });
    qc.invalidateQueries({ queryKey: queryKeys.activityFor(args.projectId) });
  }
  if (args.taskId) {
    qc.invalidateQueries({ queryKey: queryKeys.task(args.taskId) });
    qc.invalidateQueries({ queryKey: queryKeys.subtasksOf(args.taskId) });
  }
  qc.invalidateQueries({ queryKey: queryKeys.meTasks() });
  qc.invalidateQueries({ queryKey: queryKeys.meWatching() });
  // Global /tasks list — ["tasks","all"] doesn't share the per-project
  // ["tasks", projectId] prefix, so without this line board/detail
  // mutations leave that page stale for the 30s staleTime window.
  qc.invalidateQueries({ queryKey: queryKeys.tasksAll() });
  qc.invalidateQueries({ queryKey: queryKeys.inbox() });
  qc.invalidateQueries({ queryKey: queryKeys.inboxUnread() });
}

type CreateBody = {
  title: string;
  priority?: number;
  status?: TaskStatus;
  parentTaskId?: string | null;
  description?: string;
  assigneeId?: string | null;
  dueDate?: string | null;
  startDate?: string | null;
  estimate?: number | null;
  type?: "task" | "story" | "bug";
};

type CreateOpts = Omit<
  UseMutationOptions<{ task: TaskRow }, Error, CreateBody>,
  "mutationFn"
>;

// useCreateTask — invalidates the project's task list + the personal queues.
// Drop-in for TaskForm / SubtaskList.add.
// Caller's onSuccess runs AFTER our invalidations so it sees fresh data.
export function useCreateTask(projectId: string, options: CreateOpts = {}) {
  const qc = useQueryClient();
  const { onSuccess: callerOnSuccess, ...rest } = options;
  return useMutation<{ task: TaskRow }, Error, CreateBody>({
    mutationFn: (body) => api.createTask(projectId, body),
    onSuccess: (...args) => {
      invalidateForTask(qc, { projectId, taskId: args[0].task.id });
      (callerOnSuccess as ((...a: typeof args) => unknown) | undefined)?.(
        ...args,
      );
    },
    ...rest,
  });
}
